import React, { FC } from 'react';
import styles from './styles.module.css';
import {
  GetPolicyDraftResponseType,
  GetPaymentsSummaryByTermNumberResponse,
  GetPolicyResponseTypeV2,
  GetVehicleInspectionEligibilityResponse,
} from '@clearcover/core-app-types';
import { PolicyPaymentAndBilling } from '../PolicyPaymentAndBilling';
import { PolicyCancellation } from '../PolicyCancellation';
import { PolicyDocuments } from '../PolicyDocuments';
import { PolicyProducerInfo } from '../PolicyProducerInfo';
import { PolicyAccountSection } from '../PolicyAccountSection';
import { PolicyDetailSection } from '../PolicyDetailsSection';
import { PolicyDriversSection } from '../PolicyDriversSection';
import { PolicyVehiclesSection } from '../PolicyVehiclesSection';
import { PolicyCoveragesSection } from '../PolicyCoveragesSection';
import { PolicyClaimsSection } from '../policyClaimsSection';
import { PolicyVehicleSetupSection } from '../PolicyVehiclePhotos';
import { PolicyDiscountSection } from '../PolicyDiscountSection';
import { PolicyInstallmentPlanSection } from '../PolicyInstallmentPlanSection';
import { StaggeredAnimatedComponent } from '../StaggeredAnimationContainer';

import { PolicyBillingActivities } from '../PolicyBillingActivities';
import { RenewalInfo } from '../RenewalInfo';

import { Button, Asset, RateInspectorIcon } from '@clearcover/atlas';
import { useAuth0 } from 'Auth';
import { env } from 'Shared/constants/env';
import { PolicyActivities } from '../PolicyActivities/PolicyActivities';
import { usePolicyContext } from 'Policies/utils/PolicyContext/PolicyContext';
import { AxiosError, AxiosPromise } from 'axios';

type PolicyDetailsDisplayType = {
  policy?: GetPolicyResponseTypeV2 | GetPolicyDraftResponseType['policyDraft'];
  billingInfo: {
    billingInfoResponse?: GetPaymentsSummaryByTermNumberResponse;
    fetchBillingData: () => AxiosPromise<GetPaymentsSummaryByTermNumberResponse>;
    error?: AxiosError;
    loading: boolean;
  };
  vehicleInspectionEligibility?: GetVehicleInspectionEligibilityResponse;
};

export const PolicyDetailsDisplay: FC<PolicyDetailsDisplayType> = ({
  policy,
  billingInfo,
  vehicleInspectionEligibility,
}) => {
  const {
    details: { isPolicyDraft, isRenewalDraft, termId },
    systemOfRecord,
    loading,
  } = usePolicyContext();

  const { user } = useAuth0();
  const canViewQuoteInspector =
    systemOfRecord === 'clearcover' &&
    (user?.isCarrierUser || user?.isSystemUser);
  const ryersonRateId = policy?.policyDetails.ryersonRateId ?? '';

  const isNSA = policy?.policyDetails?.productSegment === 'nonStandardAuto';

  return (
    <div className={styles.container}>
      <StaggeredAnimatedComponent className={styles.firstColumn}>
        <PolicyPaymentAndBilling
          billingInfoReponse={billingInfo?.billingInfoResponse}
          fetchBillingData={billingInfo.fetchBillingData}
          termId={termId}
          loading={billingInfo?.loading}
          error={billingInfo?.error}
        />
        <PolicyDocuments />
        {policy && !isPolicyDraft && (
          <PolicyCancellation
            effectivePeriod={policy.policyDetails.effectivePeriod}
            policyStatus={policy.policyDetails.termInfo.termStatus}
            termStatusDetailList={
              policy.policyDetails.termInfo.termStatusDetailsList
            }
            renewalDraft={policy.draftList.find(
              (draft) => draft.type === 'renewal'
            )}
            isNSA={isNSA}
          />
        )}
        {policy &&
          systemOfRecord === 'clearcover' &&
          !(isRenewalDraft || isPolicyDraft) && (
            <RenewalInfo
              policyStatus={policy.policyDetails.termInfo.termStatus}
              termStatusDetailList={
                policy.policyDetails.termInfo.termStatusDetailsList
              }
              endDate={policy.policyDetails.effectivePeriod.endsAt}
            />
          )}
        <PolicyProducerInfo
          producer={policy?.producerInfo}
          policyNumber={policy?.policyDetails.policyNumber}
          policyBindReferenceId={policy?.quoteRef.sourceEntityId}
        />
        {canViewQuoteInspector && ryersonRateId !== '' && (
          <Button link size="md" className={styles.inspectRateLink}>
            <Asset
              className={styles.inspectRateLink}
              icon={RateInspectorIcon}
              height={21}
              width={27}
              description="Inspect Rate"
            />
            <a
              href={`${env.ryersonWebHostname}/quote-inspector?requestId=${ryersonRateId}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.inspectRateLink}
            >
              Inspect Rate
            </a>
          </Button>
        )}
      </StaggeredAnimatedComponent>
      <div className={styles.secondColumn}>
        <StaggeredAnimatedComponent>
          <PolicyAccountSection policyHolder={policy?.policyHolder} />
        </StaggeredAnimatedComponent>
        <StaggeredAnimatedComponent>
          <PolicyDetailSection
            policyDetails={policy?.policyDetails}
            vehicles={policy?.vehicles}
          />
        </StaggeredAnimatedComponent>
        {policy && !loading && (
          <>
            <StaggeredAnimatedComponent>
              <PolicyDriversSection
                drivers={policy.drivers}
                excludedDrivers={policy.excludedDrivers}
                vehicles={policy.vehicles}
              />
            </StaggeredAnimatedComponent>
            <StaggeredAnimatedComponent>
              <PolicyVehiclesSection
                vehicles={policy.vehicles}
                drivers={policy.drivers}
                mailingAddress={policy.policyDetails.addresses.mailing}
                isNSA={isNSA}
                showAntiTheftDevice={policy.policyDetails.showAntiTheftDevice}
              />
            </StaggeredAnimatedComponent>
            <StaggeredAnimatedComponent>
              <PolicyCoveragesSection
                vehicles={policy.vehicles}
                policyEndorsements={policy.policyEndorsements}
                policyCoverages={policy.policyCoverages}
              />
            </StaggeredAnimatedComponent>
            <StaggeredAnimatedComponent>
              <PolicyClaimsSection policy={policy} />
            </StaggeredAnimatedComponent>
            {vehicleInspectionEligibility?.isEligible && (
              <StaggeredAnimatedComponent>
                <PolicyVehicleSetupSection
                  policyId={policy.policyDetails.policyId}
                  termId={termId}
                />
              </StaggeredAnimatedComponent>
            )}
            <StaggeredAnimatedComponent>
              <PolicyDiscountSection
                discounts={policy.discounts}
                mldDiscount={policy.mldDiscount}
              />
            </StaggeredAnimatedComponent>
            <StaggeredAnimatedComponent>
              <PolicyInstallmentPlanSection
                installments={
                  billingInfo?.billingInfoResponse?.billingInfo?.installments ??
                  []
                }
              />
            </StaggeredAnimatedComponent>
            <StaggeredAnimatedComponent>
              <PolicyBillingActivities
                activities={
                  billingInfo?.billingInfoResponse?.billingInfo?.activities ??
                  []
                }
              />
            </StaggeredAnimatedComponent>
            <StaggeredAnimatedComponent>
              <PolicyActivities />
            </StaggeredAnimatedComponent>
          </>
        )}
      </div>
    </div>
  );
};
